import { GET, POST } from '@/shared/api/requests';
import type {
  ProjectTeamData,
  ProjectUser,
  InvitedUser,
  User,
  DeleteUsersPayload,
} from '../types/types';

export const projectTeamApi = {
  /**
   * Get project team data
   */
  getProjectTeam: async (clientName: string, projectName: string): Promise<ProjectTeamData> => {
    const response = await GET(`/base/projects/${clientName}/${projectName}/team/`);
    return response.data;
  },

  /**
   * Add existing internal user to project
   */
  addUserToProject: async (data: {
    project_id: number;
    user_id: number;
    client_name?: string;
    role: string;
  }): Promise<ProjectUser> => {
    const response = await POST('/base/projects/add-user/', data);
    return response.data;
  },

  /**
   * Invite new internal user to project
   */
  inviteInternalUser: async (data: {
    project_id: number;
    email: string;
    role: string;
    client_name?: string;
  }): Promise<{ success: boolean; error?: string }> => {
    const response = await POST('/base/invite-internal-user/', data);
    return response.data;
  },

  /**
   * Invite external user to project
   */
  inviteExternalUser: async (data: {
    project_id: number;
    external_email: string;
    external_company_name: string;
    role: string;
    client_name?: string;
    can_access_asbuilds?: boolean;
    can_access_progress?: boolean;
    can_access_drones?: boolean;
    can_access_drone_photos?: boolean;
    can_access_surveillance?: boolean;
  }): Promise<InvitedUser> => {
    const response = await POST('/base/projects/invite-user/', data);
    return response.data;
  },

  /**
   * Delete users from project (both ProjectUser and InvitedUser)
   */
  deleteUsers: async (payload: DeleteUsersPayload): Promise<{
    message: string;
    deleted_count: number;
    warnings?: string[];
  }> => {
    const response = await POST('/base/projects/delete-users/', payload);
    return response.data;
  },

  /**
   * Get available users for a client (for adding to project)
   */
  getClientUsers: async (clientId: number, projectId?: number): Promise<{ users: User[] }> => {
    const url = projectId 
      ? `/base/clients/${clientId}/users/?project_id=${projectId}`
      : `/base/clients/${clientId}/users/`;
    const response = await GET(url);
    return response.data;
  },

  /**
   * Get invitation details by token
   */
  getInvitationByToken: async (token: string): Promise<{ invited_user: InvitedUser }> => {
    const response = await GET(`/base/accept-invitation/?token=${token}`);
    return response.data;
  },

  /**
   * Accept invitation by token
   */
  acceptInvitation: async (token: string): Promise<{ message: string; invited_user: InvitedUser }> => {
    const response = await POST('/base/accept-invitation/', { token });
    return response.data;
  },
};

